import type React from "react";
import {useParams} from "react-router-dom";

import {
  faClock,
  faRefresh,
  faStopwatch20,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {GameServer} from "@repo/shared";

import {useLevelV2} from "~/api/hooks/useLevelV2";
import {usePersonalBestV2Query} from "~/api/hooks/usePersonalBestV2";
import AppHeader from "~/component/common/AppHeader";
import CustomButton from "~/component/common/CustomButton";
import ShareButton from "~/component/common/ShareButton";
import TimeSince from "~/component/common/TimeSince";
import UserName from "~/component/common/UserName";

import SortableTable from "../../../../component/common/SortableTable";
import type {PlayLevelParams} from "../PlayLevelParams";

const LeaderboardPage: React.FC = () => {
  const {id, playlistId} = useParams<PlayLevelParams>();
  const {data: level} = useLevelV2({id});

  // Determine a parent path based on whether we have a playlist ID
  const parentPath = playlistId
    ? `/playlist/${playlistId}/play/${id}`
    : `/play/${id}`;

  // Get top 10 players
  const {data: topPlayers, refetch} = usePersonalBestV2Query({
    key: {
      levelId: id,
    },
    sort: [
      {
        fieldPath: "replay.totalSteps",
        direction: "asc",
      },
      // TODO: filter by some other field
      // {
      //   fieldPath: "replay.timestamp",
      //   direction: "asc",
      // },
    ],
    limit: 10,
  });

  return (
    <div className="flex flex-col w-screen min-h-screen overflow-auto relative text-white bg-transparent">
      <AppHeader
        title={`${level?.name} - Leaderboard`}
        parentPath={parentPath}
      />

      <div className="fixed bottom-2 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 z-50 pointer-events-auto">
        <CustomButton
          onClick={() => refetch()}
          aria-label="Refresh"
          className="p-2 rounded-full hover:bg-black hover:bg-opacity-70 transition-colors"
        >
          <FontAwesomeIcon
            icon={faRefresh}
            size="xl"
            className="text-white p-2 -m-2 rounded-full bg-gradient-to-br from-green-600 via-green-500 to-black"
          />
        </CustomButton>
        <ShareButton />
      </div>

      <main className="mt-16 px-2 pb-4 flex-1 z-20">
        <div className="bg-black/60 rounded-lg p-4">
          <SortableTable
            data={topPlayers || []}
            columns={[
              {
                key: "levelId",
                sortable: false,
                label: "Rank",
                transformer: (_, index) => `#${index + 1}`,
              },
              {
                key: (entry) =>
                  entry.replay!.totalSteps / GameServer.STEPS_PER_SECOND,
                sortable: true,
                label: (
                  <span>
                    <FontAwesomeIcon icon={faStopwatch20} /> Time
                  </span>
                ),
                transformer: (value) => `${value.toFixed(4)}s`,
              },
              {
                key: "userId",
                sortable: true,
                label: (
                  <span>
                    <FontAwesomeIcon icon={faUser} /> Player
                  </span>
                ),
                transformer: (userId) => <UserName userId={userId} />,
              },
              {
                key: (_entry) => new Date(), //TODO fix
                sortable: true,
                label: (
                  <span>
                    <FontAwesomeIcon icon={faClock} /> Date
                  </span>
                ),
                transformer: (timestamp) => (
                  <TimeSince key={timestamp.toISOString()} date={timestamp} />
                ),
              },
            ]}
            actions={[]}
          />
        </div>
      </main>
    </div>
  );
};

export default LeaderboardPage;
